// v1.43.0: PDF export button.
// Mirrors ExportCsvButton — same props shape, same backend payload — so
// the two buttons sit side by side at every export site. The PDF endpoint
// is auth-gated on the backend; fleet exports also require promo/paid tier.
import { useState } from 'react';
import type {
  ComputeResponse,
  FleetTCOResponse,
  ScenarioComparisonResponse,
  ScenarioInput,
} from '../types';
import { exportComparePdf, exportScenarioPdf } from '../utils/api';

type ScenarioMode = 'personal' | 'fleet';

interface ScenarioPdfProps {
  kind: 'scenario';
  scenario: ScenarioInput;
  mode: ScenarioMode;
  personalResult?: ComputeResponse | null;
  fleetResult?: FleetTCOResponse | null;
}

interface ComparePdfProps {
  kind: 'compare';
  scenarioA: ScenarioInput;
  scenarioB: ScenarioInput;
  comparison: ScenarioComparisonResponse;
  mode?: ScenarioMode;
}

type Props = (ScenarioPdfProps | ComparePdfProps) & {
  className?: string;
  /** Optional override; defaults to "Export PDF". */
  label?: string;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function FileTextIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

export default function ExportPdfButton(props: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      if (props.kind === 'scenario') {
        const { blob, filename } = await exportScenarioPdf({
          scenario: props.scenario,
          mode: props.mode,
          personal_result: props.mode === 'personal' ? props.personalResult ?? null : null,
          fleet_result: props.mode === 'fleet' ? props.fleetResult ?? null : null,
        });
        downloadBlob(blob, filename);
      } else {
        const { blob, filename } = await exportComparePdf({
          scenario_a: props.scenarioA,
          scenario_b: props.scenarioB,
          comparison: props.comparison,
          mode: props.mode ?? 'fleet',
        });
        downloadBlob(blob, filename);
      }
    } catch {
      setError('PDF failed — try again');
      setTimeout(() => setError(null), 4000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={props.className} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
        data-testid="export-pdf-button"
      >
        <FileTextIcon />
        {busy ? 'Generating PDF…' : (props.label ?? 'Export PDF')}
      </button>
      {error && (
        <span style={{ fontSize: 12, color: 'var(--tco-warn, #b91c1c)' }} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
